import { useState } from "react";
import { X, Search, Package, Ban, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import { apiFetch } from "../../api/apiFetch";
import { useTranslation } from "../../i18n";
import { useReadOnly } from "../../access/ReadOnly";
import FlowLoader from "../FlowLoader";
import type { DispatchOrder, DispatchOrderItem } from "../../types/Dispatch";

type SearchField = "sku" | "ean" | "title";

type ProductHit = {
    sku: string;
    title: string | null;
    barcode: string | null;
    image_url: string | null;
    weight_grams: number | null;
};

type Props = {
    order: DispatchOrder;
    item: DispatchOrderItem;
    onMapped: (updatedOrder: DispatchOrder) => void;
    onClose: () => void;
};

const SkuMappingModal = ({ order, item, onMapped, onClose }: Props) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [field, setField] = useState<SearchField>("sku");
    const [q, setQ] = useState("");
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState<ProductHit[] | null>(null);
    const [mapping, setMapping] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSearch = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!q.trim()) return;
        setSearching(true);
        setError(null);
        setResults(null);
        try {
            const res = await apiFetch(
                `/products/exact-search?field=${field}&q=${encodeURIComponent(q.trim())}`,
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setResults(Array.isArray(data.products) ? data.products : []);
        } catch {
            setError(t("sku_map_search_failed"));
        } finally {
            setSearching(false);
        }
    };

    const handleMap = async (targetSku: string) => {
        if (readOnly || item.line_position == null) return;
        setMapping(true);
        setError(null);
        try {
            const res = await apiFetch(
                `/orders/${encodeURIComponent(order.order_id)}/items/${item.line_position}/map-sku`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ sku: targetSku }),
                },
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data?.message || t("sku_map_failed"));
                setMapping(false);
                return;
            }
            toast.success(t("sku_map_success"));
            onMapped(data.order);
        } catch {
            setError(t("sku_map_failed"));
            setMapping(false);
        }
    };

    const handleNonStock = async () => {
        if (readOnly || item.line_position == null) return;
        setMapping(true);
        setError(null);
        try {
            const res = await apiFetch(
                `/orders/${encodeURIComponent(order.order_id)}/items/${item.line_position}/non-stock`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ is_non_stock: true }),
                },
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data?.message || t("sku_map_failed"));
                setMapping(false);
                return;
            }
            toast.success(t("sku_map_non_stock_marked"));
            onMapped(data.order);
        } catch {
            setError(t("sku_map_failed"));
            setMapping(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal-card sku-map-modal"
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: 640 }}
            >
                <div className="modal-card-header">
                    <h2>{t("sku_map_title")}</h2>
                    <button className="modal-close" onClick={onClose} aria-label={t("close")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    <div className="sku-map-context">
                        <p className="sku-map-context-label">{t("sku_map_unmapped_label")}</p>
                        <p className="sku-map-context-sku">{item.external_sku || item.sku}</p>
                        {item.title && (
                            <p className="sku-map-context-title">{item.title}</p>
                        )}
                    </div>

                    <form className="sku-map-search" onSubmit={handleSearch}>
                        <div className="sku-map-field-toggle" role="radiogroup" aria-label={t("sku_map_search_by")}>
                            {(["sku", "ean", "title"] as SearchField[]).map((f) => (
                                <button
                                    key={f}
                                    type="button"
                                    role="radio"
                                    aria-checked={field === f}
                                    className={`sku-map-field-btn${field === f ? " sku-map-field-btn--active" : ""}`}
                                    onClick={() => { setField(f); setResults(null); }}
                                >
                                    {t(`sku_map_field_${f}`)}
                                </button>
                            ))}
                        </div>
                        <div className="sku-map-input-row">
                            <input
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={t(`sku_map_placeholder_${field}`)}
                                className="sku-map-input"
                                autoFocus
                            />
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={!q.trim() || searching}
                            >
                                <Search size={16} />
                                {t("search")}
                            </button>
                        </div>
                    </form>

                    {error && (
                        <div className="dispatch-error-banner" role="alert">
                            <AlertCircle size={18} />
                            <span>{error}</span>
                        </div>
                    )}

                    {searching && (
                        <div style={{ display: "flex", justifyContent: "center", padding: 16 }}>
                            <FlowLoader message={t("loading")} />
                        </div>
                    )}

                    {results !== null && !searching && (
                        <div className="sku-map-results">
                            {results.length === 0 ? (
                                <p className="sku-map-empty">{t("sku_map_no_results")}</p>
                            ) : (
                                results.map((p) => (
                                    <button
                                        key={p.sku}
                                        type="button"
                                        className="sku-map-result"
                                        disabled={mapping || readOnly}
                                        aria-disabled={readOnly}
                                        title={readOnly ? t("read_only_mode") : undefined}
                                        onClick={() => handleMap(p.sku)}
                                    >
                                        {p.image_url ? (
                                            <img src={p.image_url} alt="" className="sku-map-result-img" />
                                        ) : (
                                            <div className="sku-map-result-img sku-map-result-img--ph">
                                                <Package size={16} />
                                            </div>
                                        )}
                                        <div className="sku-map-result-info">
                                            <p className="sku-map-result-title">{p.title || p.sku}</p>
                                            <p className="sku-map-result-meta">
                                                <span>{p.sku}</span>
                                                {p.barcode && <span> · {p.barcode}</span>}
                                            </p>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    )}

                    <div className="sku-map-footer">
                        <button
                            type="button"
                            className="btn btn-ghost sku-map-non-stock"
                            onClick={handleNonStock}
                            disabled={mapping || readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : t("sku_map_non_stock_hint")}
                        >
                            <Ban size={16} />
                            {t("sku_map_mark_non_stock")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SkuMappingModal;
